import React, { useEffect, useMemo, useState } from "react";

/**
 * Modal to move a library folder - with everything inside it - or a single section into
 * another folder of the same library, at any depth, or back to Home. Backed by the
 * web-service LibraryModuleAdminController:
 *   GET   /my-jeeva-api/library-module-admin/folder-outline/{libraryModuleId}
 *   PATCH /my-jeeva-api/library-module-admin/folder-move/{folderId}/{targetFolderId}
 *   PATCH /my-jeeva-api/library-module-admin/section-move/{sectionId}/{targetFolderId}
 *
 * Props:
 *   libraryModuleId  string  UUID of the library the item belongs to
 *   kind             string  "folder" or "section"
 *   item             object  the folder ({ id, label, parentId }) or section
 *                            ({ id, folderId, caption, textContent, type }) being moved
 *   onMoved          fn      called with the destination ({ id, label, path }) after a move
 *   onClose          fn
 */
const HOME_FOLDER_ID = "00000000-0000-0000-0000-000000000000";

function authHeaders() {
  return { Authorization: "Bearer " + (localStorage.getItem("access_token") || "") };
}

// Sections have no title: name them by caption, else the start of their text, else their type
function sectionName(section) {
  if (section.caption && section.caption.trim()) return section.caption.trim();
  const div = document.createElement("div");
  div.innerHTML = section.textContent || "";
  const text = (div.textContent || "").replace(/\s+/g, " ").trim();
  if (text) return text.length > 50 ? text.substring(0, 50) + "…" : text;
  const type = (section.type || section.sectionType || "").toLowerCase();
  return type ? type + " section" : "section";
}

export default function MoveToFolderPanel({ libraryModuleId, kind, item, onMoved, onClose }) {
  const [outline, setOutline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [targetId, setTargetId] = useState(null);
  const [filter, setFilter] = useState("");
  const [moving, setMoving] = useState(false);
  const [status, setStatus] = useState("");

  useEffect(() => {
    fetch("/my-jeeva-api/library-module-admin/folder-outline/" + libraryModuleId, { headers: authHeaders() })
      .then((r) => r.json())
      .then((res) => {
        if (res && res.success) {
          setOutline(res.result || []);
        } else {
          setStatus((res && res.result) || "Could not load the folders.");
        }
      })
      .catch((err) => {
        console.error("Error loading folder outline:", err);
        setStatus("Could not load the folders.");
      })
      .finally(() => setLoading(false));
  }, [libraryModuleId]);

  const isFolder = kind === "folder";
  const currentParentId = (isFolder ? item.parentId : item.folderId) || HOME_FOLDER_ID;
  const name = isFolder ? item.label : sectionName(item);
  // A section has no subtree, so nothing has to be left out of its destinations
  const excludedId = isFolder ? item.id : null;

  // Depth-first list of every folder the item may land in, with its depth and path.
  // A moved folder's own subtree is left out: it cannot be moved into itself.
  const destinations = useMemo(() => {
    const children = {};
    outline.forEach((f) => {
      (children[f.parentId] = children[f.parentId] || []).push(f);
    });
    // Same order as the library shows each folder's subfolders
    Object.values(children).forEach((list) =>
      list.sort((a, b) => (a.order || 0) - (b.order || 0))
    );

    const result = [{ id: HOME_FOLDER_ID, label: "Home", depth: 0, path: "Home" }];
    const visited = new Set([HOME_FOLDER_ID]);
    const walk = (parentId, depth, path) => {
      (children[parentId] || []).forEach((f) => {
        if (f.id === excludedId || visited.has(f.id)) return;
        visited.add(f.id);
        const fullPath = path + " › " + (f.label || "Untitled");
        result.push({ id: f.id, label: f.label || "Untitled", depth, path: fullPath });
        walk(f.id, depth + 1, fullPath);
      });
    };
    walk(HOME_FOLDER_ID, 1, "Home");
    return result;
  }, [outline, excludedId]);

  const query = filter.trim().toLowerCase();
  const shown = query
    ? destinations.filter((d) => d.path.toLowerCase().includes(query))
    : destinations;
  const target = destinations.find((d) => d.id === targetId);

  const move = () => {
    if (!targetId) return;
    setMoving(true);
    setStatus("");
    const moveUrl = isFolder
      ? "/my-jeeva-api/library-module-admin/folder-move/"
      : "/my-jeeva-api/library-module-admin/section-move/";
    fetch(moveUrl + item.id + "/" + targetId, {
      method: "PATCH",
      headers: authHeaders(),
    })
      .then((r) => r.json())
      .then((res) => {
        if (res && res.success) {
          onMoved(target);
        } else {
          setStatus((res && res.result) || "Move failed.");
        }
      })
      .catch((err) => {
        console.error("Error moving " + kind + ":", err);
        setStatus("Move failed.");
      })
      .finally(() => setMoving(false));
  };

  const overlay = {
    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
    background: "rgba(0,0,0,0.5)", zIndex: 100000,
    display: "flex", alignItems: "center", justifyContent: "center",
  };
  const card = {
    background: "#fff", borderRadius: 8, width: "min(560px, 94vw)",
    maxHeight: "90vh", display: "flex", flexDirection: "column",
    padding: 20, boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h4 style={{ margin: 0 }}>Move {isFolder ? "folder" : "section"} “{name}” to…</h4>
          <button onClick={onClose} style={{ border: "none", background: "transparent", fontSize: 22, cursor: "pointer" }}>×</button>
        </div>
        {isFolder && (
          <p style={{ margin: "0 0 10px", color: "#555" }}>
            Everything inside this folder moves with it.
          </p>
        )}

        <input
          type="text"
          placeholder="Search folders"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ padding: "6px 8px", marginBottom: 10, border: "1px solid #ccc", borderRadius: 4 }}
        />

        <div style={{ flex: 1, minHeight: 200, overflowY: "auto", border: "1px solid #D2D4DC", borderRadius: 6 }}>
          {loading ? (
            <div style={{ padding: 12, color: "#888" }}>Loading…</div>
          ) : shown.length === 0 ? (
            <div style={{ padding: 12, color: "#888" }}>No matching folders.</div>
          ) : (
            shown.map((d) => {
              const isCurrent = d.id === currentParentId;
              const isSelected = d.id === targetId;
              return (
                <div
                  key={d.id}
                  title={d.path}
                  onClick={isCurrent ? null : () => setTargetId(d.id)}
                  style={{
                    padding: "7px 10px",
                    paddingLeft: query ? 10 : 10 + d.depth * 20,
                    cursor: isCurrent ? "default" : "pointer",
                    background: isSelected ? "#282a65" : "transparent",
                    color: isSelected ? "#fff" : isCurrent ? "#999" : "#222",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  {query ? d.path : d.label}
                  {isCurrent && <span style={{ marginLeft: 8, fontSize: 12 }}>(current location)</span>}
                </div>
              );
            })
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 12, marginTop: 14 }}>
          {status && <span style={{ color: "#c62828", marginRight: "auto" }}>{status}</span>}
          <button onClick={onClose} style={{ padding: "6px 16px" }}>Cancel</button>
          <button onClick={move} disabled={!targetId || moving}
            style={{ padding: "6px 16px", background: targetId ? "#282a65" : "#282a6596", color: "#fff", border: "none", borderRadius: 4, cursor: targetId ? "pointer" : "default" }}>
            {moving ? "Moving…" : "Move"}
          </button>
        </div>
      </div>
    </div>
  );
}



// WEBPACK FOOTER //
// src/my-jeeva/projectmgmt/MoveToFolderPanel.js